// Procurement > Competitor Pricing — our steamed blue crab prices against the
// five competitors Matt Sr named, matched by pack size.
//
// The screen derives NOTHING. Every price, every age and the stale flag are
// computed once in comp-scrape.js and rendered here, so this page and anything
// that emails the same numbers later cannot drift apart.
//
// ⚠️ A BLANK CELL AND AN OLD CELL MUST NEVER LOOK THE SAME. Blank means that
// competitor does not sell that pack size in that grade — a real fact worth
// pricing against. Old means we could not read their site today and this is the
// last thing we knew. They are rendered differently on purpose (an em space vs
// a dimmed price with its age), because the whole value of the report is Matt
// trusting a number enough to move a price on it.

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ExternalLink, AlertTriangle, Check, Clock } from 'lucide-react';
import { competitorPricing } from '../services/api';

const money = (n) => '$' + Number(n).toFixed(2);

// Matched on point-to-point inches wherever both sides publish one. "Large" is a
// different crab at every one of these five companies, which is exactly why the
// spec asked for "each competitor's closest equivalent" rather than a name join.
//
// Two of the five publish no size at all - Linton's and Harbour House sell
// Standard / Large / Jumbo / Colossal with no measurement anywhere on the page.
// Refusing to match them leaves three quarters of the report empty against
// competitors we plainly do compete with, so there is a SECOND, WEAKER match:
// line their male ladder up against ours by POSITION, their third-largest grade
// against our third-largest.
//
// ⚠️ THE TWO MATCHES ARE NOT THE SAME CLAIM AND THE SCREEN MUST NOT PRETEND
// THEY ARE. A size match says "this is the same crab". A ladder match says
// "this is the same rung of their range", which is a guess about how they grade,
// and the row says so. Never silently promote one to the other.
const GRADE_RANK = {
  Standard: 1, Medium: 2, Large: 3, Premium: 3,
  Jumbo: 4, 'X-Large': 4, Colossal: 5, Super: 5, Female: 1,
};

function closestGrade(compGrades, ourRange, gender, ourRank) {
  const all = Object.values(compGrades).filter((g) => g.gender === gender);
  if (!all.length) return null;

  const sized = all.filter((g) => g.sizeMinIn != null);
  if (sized.length && ourRange) {
    const mid = (g) => (g.sizeMaxIn != null ? (g.sizeMinIn + g.sizeMaxIn) / 2 : g.sizeMinIn + 0.25);
    const target = (ourRange[0] + ourRange[1]) / 2;
    let best = null;
    let bestGap = Infinity;
    for (const g of sized) {
      const gap = Math.abs(mid(g) - target);
      if (gap < bestGap) { bestGap = gap; best = g; }
    }
    // Beyond about three quarters of an inch apart these are not the same crab,
    // and a forced match is worse than an honest blank.
    if (bestGap <= 0.75) return { ...best, matchedBy: 'size' };
  }

  // Ladder fallback, only for a competitor who publishes no sizes at all. If
  // they published sizes and none of them was close enough, that is a real
  // "they do not sell this crab" and it stays blank.
  if (sized.length || !ourRank) return null;
  const ladder = all
    .map((g) => ({ g, rank: GRADE_RANK[g.grade] || 0 }))
    .filter((x) => x.rank)
    .sort((a, b) => a.rank - b.rank);
  if (!ladder.length) return null;
  const idx = Math.min(ourRank, ladder.length) - 1;
  return { ...ladder[idx].g, matchedBy: 'ladder' };
}

// Our own grades' point-to-point ranges. Source: the live PDP size chart.
const OUR_SIZE_IN = {
  STMHC: [5.0, 5.5],
  SLMHC: [5.5, 6.0],
  PMHC: [6.0, 6.5],
  CMHC: [6.5, 7.0],
  STFHC: [5.5, 6.5],
  SLFHC: [6.5, 7.0],
};

export default function CompetitorPricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  // steamed_blue_crabs | live_blue_crabs - live added 2026-10-30 when App01's Extractor was retired.
  const [category, setCategory] = useState('steamed_blue_crabs');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      setData(await competitorPricing('report', { category }));
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  // "Refresh now" is Matt's on-demand trigger from the spec. It fires the
  // background worker and polls, rather than waiting on the request: a
  // synchronous call would hit the gateway's ~26s cap the first time a
  // competitor's site was slow.
  const refresh = async () => {
    setRunning(true);
    setNote('Reading every competitor site…');
    try {
      await competitorPricing('run', { force: true });
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const s = await competitorPricing('status');
          const recent = (s.runs || []).filter(
            (r) => new Date(r.started_at).getTime() > Date.now() - 5 * 60 * 1000
          );
          const done = recent.filter((r) => r.finished_at);
          if (done.length >= (s.expected || 5) || tries > 20) {
            clearInterval(poll);
            setRunning(false);
            const failed = done.filter((r) => !r.ok);
            setNote(
              failed.length
                ? failed.length + ' of ' + done.length + ' could not be read: ' +
                  failed.map((r) => r.domain).join(', ')
                : 'All ' + done.length + ' read.'
            );
            load();
          }
        } catch (e) {
          clearInterval(poll);
          setRunning(false);
          setErr(e.message);
        }
      }, 3000);
    } catch (e) {
      setRunning(false);
      setErr(e.message);
    }
  };

  if (loading && !data) {
    return <div className="card"><div className="card-body">Loading…</div></div>;
  }

  return (
    <div>
      <div className="breadcrumb">
        <a href="#procurement">Procurement</a>
        <span> / </span>
        <span>Competitor Pricing</span>
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, marginBottom: 16, flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Competitor Pricing</h1>
          <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
            {category === 'live_blue_crabs' ? 'Live' : 'Steamed'} hard crabs, matched by pack size. Reads automatically every
            morning at 7:12; use Refresh now for a fresh look any time.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(data.categories || [{ key: 'steamed_blue_crabs', label: 'Steamed' }]).map((c) => (
            <button
              key={c.key}
              className={'btn ' + (c.key === category ? 'btn-primary' : 'btn-outline')}
              onClick={() => setCategory(c.key)}
              disabled={loading}
            >
              {c.label}
            </button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={refresh} disabled={running}>
          <RefreshCw size={15} style={{ marginRight: 6 }} />
          {running ? 'Reading…' : 'Refresh now'}
        </button>
      </div>

      {err ? (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)' }}>
          <div className="card-body" style={{ color: 'var(--orange)' }}>{err}</div>
        </div>
      ) : null}
      {note ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ fontSize: 13 }}>{note}</div>
        </div>
      ) : null}

      <SourceHealth competitors={data.competitors} />

      {data.ourGrades.map((g) => (
        <GradeTable
          key={g.code}
          ourGrade={g}
          ourPrices={data.ours[g.code] || {}}
          units={data.units}
          unitLabels={data.unitLabels}
          competitors={data.competitors}
        />
      ))}

      <div style={{ color: '#777', fontSize: 12, marginTop: 8 }}>
        A blank cell means that competitor does not sell that pack size in the
        matched grade. A dimmed price with a clock is the last price we could
        read, not today's. Competitors are matched to our grades on
        point-to-point size, never on the grade name.
      </div>
    </div>
  );
}

// Where every number came from and when. This is the panel that stops a stale
// price being read as a price cut.
function SourceHealth({ competitors }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">Where these numbers come from</div>
      <div className="card-body" style={{ padding: 0 }}>
        <table className="product-table">
          <thead>
            <tr>
              <th>Competitor</th>
              <th>Last read</th>
              <th>Rows</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {competitors.map((c) => {
              const r = c.lastRun;
              const when = r ? new Date(r.at) : null;
              const mins = when ? Math.round((Date.now() - when.getTime()) / 60000) : null;
              return (
                <tr key={c.domain}>
                  <td>
                    <a href={'https://' + c.domain} target="_blank" rel="noreferrer">
                      {c.name} <ExternalLink size={11} />
                    </a>
                  </td>
                  <td>
                    {!r ? (
                      <span style={{ color: '#999' }}>never</span>
                    ) : r.ok ? (
                      <span style={{ color: 'green' }}>
                        <Check size={13} />{' '}
                        {mins < 60 ? mins + ' min ago' : Math.round(mins / 60) + ' h ago'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--orange)' }}>
                        <AlertTriangle size={13} /> failed
                      </span>
                    )}
                  </td>
                  <td>{r ? r.rows : '—'}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {r && !r.ok ? r.error : null}
                    {c.needsRender ? (
                      <span>
                        Their other pack sizes load by script, so only the size
                        their page opens on is read.
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GradeTable({ ourGrade, ourPrices, units, unitLabels, competitors }) {
  const ourRange = OUR_SIZE_IN[ourGrade.code];
  const rows = competitors.map((c) => ({
    competitor: c,
    match: closestGrade(c.data.grades, ourRange, ourGrade.gender, ourGrade.rank),
  }));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        {ourGrade.label}
        {ourRange ? (
          <span style={{ fontWeight: 400, color: '#777', marginLeft: 8, fontSize: 12 }}>
            {ourRange[0]}" to {ourRange[1]}" point to point
          </span>
        ) : null}
      </div>
      <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="product-table">
          <thead>
            <tr>
              <th style={{ minWidth: 230 }}>Product / grade</th>
              {units.map((u) => (
                <th key={u} style={{ textAlign: 'right' }}>{unitLabels[u]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'var(--peach)', fontWeight: 600 }}>
              <td>The Crab Place — {ourGrade.label}</td>
              {units.map((u) => (
                <td key={u} style={{ textAlign: 'right' }}>
                  {ourPrices[u] ? money(ourPrices[u]) : <span style={{ color: '#999' }}>—</span>}
                </td>
              ))}
            </tr>
            {rows.map(({ competitor, match }) => (
              <tr key={competitor.domain}>
                <td>
                  <div>{competitor.name}</div>
                  <div style={{ fontSize: 11, color: '#777' }}>
                    {match ? (
                      <>
                        {match.grade}
                        {match.sizeMinIn != null
                          ? ' · ' + match.sizeMinIn + '"' +
                            (match.sizeMaxIn != null ? ' to ' + match.sizeMaxIn + '"' : ' and up')
                          : ''}
                        {match.matchedBy === 'ladder' ? (
                          <span style={{ color: 'var(--orange)' }}>
                            {' · '}they publish no size, matched by position in their range
                          </span>
                        ) : null}
                      </>
                    ) : (
                      'no equivalent grade'
                    )}
                  </div>
                </td>
                {units.map((u) => {
                  const cell = match && match.units[u];
                  const ours = ourPrices[u];
                  if (!cell) {
                    return (
                      <td key={u} style={{ textAlign: 'right', color: '#ccc' }}>
                        &mdash;
                      </td>
                    );
                  }
                  // Cheaper than us is the thing Matt is looking for, so it is
                  // the only thing coloured.
                  const under = ours && cell.price < ours;
                  return (
                    <td
                      key={u}
                      style={{
                        textAlign: 'right',
                        color: cell.stale ? '#aaa' : under ? 'var(--orange)' : 'inherit',
                        fontWeight: under && !cell.stale ? 600 : 400,
                      }}
                      title={
                        cell.stale
                          ? 'Last read ' + cell.ageDays + ' days ago, not today'
                          : ours
                            ? (under ? 'under us by ' : 'over us by ') +
                              money(Math.abs(cell.price - ours))
                            : ''
                      }
                    >
                      {money(cell.price)}
                      {cell.stale ? <Clock size={11} style={{ marginLeft: 4 }} /> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
